import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, PlaneTakeoff, Wrench, XCircle } from 'lucide-react';
import { getSetting, SETTING_KEYS, setSetting } from '@/db/repositories/settings';
import { formatDateTime } from '@/lib/format';
import {
  repairReadiness,
  verifyOfflineReadiness,
  type CheckStatus,
  type ReadinessReport,
} from '@/services/offline/OfflineReadinessService';

const ICON: Record<CheckStatus, typeof CheckCircle2> = {
  ok: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
};

const TONE: Record<CheckStatus, string> = {
  ok: 'text-emerald-400',
  warning: 'text-amber-400',
  error: 'text-red-400',
};

/** Modo "pronto para viajar" (PLAN §20). */
export function OfflineReadinessCard(): React.ReactElement {
  const [report, setReport] = useState<ReadinessReport | null>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<string | undefined>(undefined);
  const [repairing, setRepairing] = useState(false);

  useEffect(() => {
    void getSetting<string | undefined>(SETTING_KEYS.lastOfflineCheck, undefined).then(setLastCheckedAt);
  }, []);

  const verify = useCallback(async () => {
    setRunning(true);
    setProgress({ done: 0, total: 0 });

    try {
      const next = await verifyOfflineReadiness({
        // Checksum lê cada arquivo inteiro; só vale quando há poucos.
        verifyChecksums: false,
        onProgress: (done, total) => setProgress({ done, total }),
      });

      setReport(next);
      setLastCheckedAt(next.checkedAt);
      await setSetting(SETTING_KEYS.lastOfflineCheck, next.checkedAt);
    } finally {
      setRunning(false);
      setProgress(null);
    }
  }, []);

  const repair = useCallback(async () => {
    if (!report) return;
    setRepairing(true);
    try {
      await repairReadiness(report);
      await verify();
    } finally {
      setRepairing(false);
    }
  }, [report, verify]);

  const problems = report
    ? report.videos.missing.length +
      report.videos.sizeMismatch.length +
      report.videos.checksumMismatch.length
    : 0;

  return (
    <section className="rounded-xl bg-slate-800 p-4">
      <h2 className="flex items-center gap-2 font-medium">
        <PlaneTakeoff size={18} aria-hidden />
        Pronto para viajar
      </h2>

      <p className="mt-1 text-sm text-slate-400">
        Confere no dispositivo — e não no banco de dados — se o app, o catálogo e os
        vídeos estão mesmo disponíveis sem internet.
      </p>

      <button
        type="button"
        onClick={() => void verify()}
        disabled={running}
        className="mt-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-sky-500 text-sm font-medium text-slate-950 active:bg-sky-600 disabled:bg-slate-700 disabled:text-slate-400"
      >
        {running
          ? progress && progress.total > 0
            ? `Verificando ${progress.done}/${progress.total}…`
            : 'Verificando…'
          : 'Verificar conteúdo offline'}
      </button>

      {report && (
        <div className="mt-4" role="status">
          <p
            className={`text-sm font-semibold ${report.ready ? 'text-emerald-300' : 'text-red-300'}`}
          >
            {report.ready ? 'PRONTO PARA USO OFFLINE' : 'NÃO ESTÁ PRONTO'}
          </p>

          <ul className="mt-3 space-y-2">
            {report.checks.map((check) => {
              const Icon = ICON[check.status];
              return (
                <li key={check.id} className="flex items-start gap-2 text-sm">
                  <Icon size={16} className={`mt-0.5 shrink-0 ${TONE[check.status]}`} aria-hidden />
                  <span className="min-w-0">
                    <span className="font-medium">{check.label}</span>
                    <span className="block text-slate-400">{check.detail}</span>
                  </span>
                </li>
              );
            })}
          </ul>

          {report.videos.expected > 0 && (
            <p className="mt-3 text-sm tabular-nums text-slate-300">
              Vídeos {report.videos.present} / {report.videos.expected} disponíveis offline
            </p>
          )}

          {report.videos.orphans.length > 0 && (
            <p className="mt-2 text-xs text-slate-500">
              {report.videos.orphans.length} arquivo(s) sem sinal correspondente no catálogo.
              Foram mantidos: o sinal pode voltar numa próxima atualização.
            </p>
          )}

          {problems > 0 && (
            <button
              type="button"
              onClick={() => void repair()}
              disabled={repairing}
              className="mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-slate-700 text-sm font-medium active:bg-slate-600 disabled:opacity-60"
            >
              <Wrench size={16} aria-hidden />
              Corrigir {problems} {problems === 1 ? 'problema' : 'problemas'}
            </button>
          )}
        </div>
      )}

      <p className="mt-3 text-xs text-slate-500">
        Última verificação: {formatDateTime(lastCheckedAt)}
      </p>
    </section>
  );
}
